// Grammar Units 4 Data - Unit 4 (Combined from Ch04 1st Half and 2nd Half)
// Auxiliary Verbs and Modal Verbs Complete Course
// Organized for Supabase migration and systematic data management

export const grammarUnits4Data = [
  {
    id: 4,
    title: { traditional: '助動詞與情態動詞完整學習', simplified: '助动词与情态动词完整学习', english: 'Complete Auxiliary and Modal Verbs Learning' },
    subtitle: { traditional: '掌握助動詞與情態動詞的完整用法', simplified: '掌握助动词与情态动词的完整用法', english: 'Master complete auxiliary and modal verb usage' },
    description: { traditional: '學習英文助動詞的基本概念，包括一般動詞功能的助動詞、情態助動詞的用法，以及進階的情態動詞應用。', simplified: '学习英文助动词的基本概念，包括一般动词功能的助动词、情态助动词的用法，以及进阶的情态动词应用。', english: 'Learn basic English auxiliary verbs including primary auxiliary verbs, modal auxiliary verbs, and advanced modal verb applications.' },
    icon: '🔧',
    color: 'from-orange-500 to-red-600',
    status: 'notStarted',
    difficulty: 'intermediate',
    estimatedHours: 4,
    content: {
      // Part 1: Basic Auxiliary Verbs (from Ch04 1st Half)
      basicAuxiliaryVerbs: {
        title: "助動詞基礎 - Basic Auxiliary Verbs",
        subtitle: "be / do / have",
        types: [
          {
            name: "一般動詞功能的助動詞",
            verbs: ["be", "do", "have"],
            description: "用於形成時態、語態等基本語法結構"
          },
          {
            name: "情態助動詞",
            verbs: ["can", "could", "may", "might", "must", "shall", "should", "will", "would"],
            description: "表達能力、可能性、必要性等情態意義"
          }
        ],
        usages: [
          {
            type: "肯定句",
            pattern: "助動詞 + 一般動詞",
            examples: [
              "Donna must finish her homework before she watches TV.",
              "We might go to Kenting this weekend."
            ]
          },
          {
            type: "疑問句", 
            pattern: "助動詞 + 主詞 + 一般動詞",
            examples: [
              "Does this study confirm our theory?",
              "Will Lisa get a scholarship this year?"
            ]
          },
          {
            type: "否定句",
            pattern: "助動詞 + not + 一般動詞", 
            examples: [
              "He didn't move to Vienna last year.",
              "He can't play the piano very well."
            ]
          }
        ],
        conjugations: {
          title: "助動詞變化表",
          verbs: [
            { base: "can", past: "could", pastParticiple: "×" },
            { base: "may", past: "might", pastParticiple: "×" },
            { base: "will", past: "would", pastParticiple: "×" },
            { base: "shall", past: "should", pastParticiple: "×" },
            { base: "am/is/are", past: "was/were", pastParticiple: "been" },
            { base: "have/has", past: "had", pastParticiple: "×" },
            { base: "do/does", past: "did", pastParticiple: "×" },
            { base: "must", past: "had to", pastParticiple: "×" }
          ]
        }
      },

      // Part 2: May/Might (from Ch04 1st Half)
      mayMight: {
        title: "情態助動詞 may / might",
        subtitle: "可能性表達",
        explanation: "may 和 might 都表示可能性，might 的不確定性比 may 高",
        scale: {
          title: "可能性程度",
          items: ["might (較低可能性)", "may (較高可能性)"]
        },
        usages: [
          {
            function: "表示可能性",
            examples: [
              "It may rain tomorrow. (明天可能會下雨)",
              "It might rain tomorrow. (明天可能會下雨，不過可能性較低)"
            ]
          },
          {
            function: "詢問許可與否",
            examples: [
              "May I come in? (我可以進來嗎？)",
              "Now that you have finished your job, you may go home now."
            ]
          },
          {
            function: "表示希望、祝福",
            examples: [
              "May your days be filled with happiness.",
              "May luck be with you."
            ]
          }
        ],
        patterns: [
          {
            name: "may well + 原形動詞",
            meaning: "很有可能......；大可......",
            example: "The Warriors may well win the championship."
          },
          {
            name: "might as well + 原形動詞", 
            meaning: "不妨......；倒不如......",
            example: "There's no reason for you to stay, so you might as well go home."
          }
        ]
      },

      // Part 3: Can/Could (from Ch04 1st Half)
      canCould: {
        title: "情態助動詞 can / could",
        subtitle: "能力與可能性",
        comparison: {
          can: ["現在式", "未來式"],
          could: ["過去式"],
          "be able to": ["現在式", "過去式", "未來式"]
        },
        usages: [
          {
            function: "表示能力",
            examples: [
              "They can/are able to play soccer well.",
              "By the time she was seven, she could/was able to speak three languages.",
              "The math genius is able to calculate as fast as a computer."
            ]
          },
          {
            function: "表示某事發生的可能性",
            examples: [
              "I don't think he can get everything done in one day.",
              "He can seldom find time to take a vacation.",
              "Can the news be true?"
            ]
          },
          {
            function: "詢問許可與否",
            examples: [
              "Tom, can I use your computer when you go to work?",
              "Could I have another piece of cake?"
            ]
          }
        ],
        patterns: [
          {
            name: "can't help but + 原形動詞",
            meaning: "忍不住......；不得不......",
            example: "Linda couldn't help but give them money."
          },
          {
            name: "cannot...too",
            meaning: "再......也不為過",
            alternatives: ["can never...too", "can never...enough"],
            examples: [
              "When you go into a desert, you cannot take too much water.",
              "If you are not enjoying a movie, it can never end too soon."
            ]
          }
        ]
      },

      // Part 4: Will/Would (from Ch04 1st Half)
      willWould: {
        title: "情態助動詞 will / would",
        subtitle: "意願與習慣",
        will: {
          functions: [
            "較直接，常用於朋友、熟人間",
            "will 可表示意願或決心",
            "否定常用 won't 來表示"
          ],
          examples: [
            "Will you shut the door, please?",
            "I will go on pursuing my goal no matter what you say.",
            "Please forgive me. I won't make the same mistake again."
          ]
        },
        would: {
          functions: [
            "較正式、客氣，常用於長輩或陌生人之間",
            "would 可表示「過去的習慣」，與 used to 意思相同",
            "否定的 wouldn't 表示「拒絕、不願意；無法」，與 didn't use to 不同"
          ],
          examples: [
            "Would you please do me a favor?",
            "I used to go see a movie once a week. → I would go see a movie once a week.",
            "Mark wouldn't meet the deadline.",
            "Tom wouldn't give his brother any money."
          ]
        },
        politeRequests: {
          title: "Would you mind 禮貌用語",
          patterns: [
            "Would you mind if I + 過去式動詞",
            "Would you mind + V-ing...",
            "Would you mind + 代名詞(受格/所有格) + V-ing"
          ],
          examples: [
            "Would you mind if I left class early today?",
            "Would you mind going to the store to get some eggs?",
            "Would you mind opening the door?",
            "Would you mind me/my opening the door?"
          ]
        }
      },

      // Part 5: Advanced Modal Verbs (from Ch04 2nd Half)
      advancedModals: {
        title: "進階情態動詞應用",
        sections: [
          {
            subtitle: "Would - 禮貌請求與偏好",
            sections: [
              {
                subtitle: "禮貌請求",
                pattern: "Would you + V...?",
                usage: "Express polite requests",
                examples: [
                  "Would you help me with this?",
                  "Would you mind closing the window?"
                ],
                chinese: "禮貌的請求"
              },
              {
                subtitle: "Would Rather",
                pattern: "would rather + base verb / would rather + V₁ than + V₂",
                usage: "Express preferences",
                examples: [
                  "I would rather have my sandwich without pickles.",
                  "Austin would rather go to the movies than go dancing."
                ],
                chinese: "寧願、較喜歡"
              }
            ]
          },
          {
            subtitle: "Must / Have to / Have got to",
            sections: [
              {
                subtitle: "必要性與義務",
                pattern: "must / have to + base verb",
                usage: "Express necessity, responsibility, or duty",
                examples: [
                  "She had to burn the midnight oil to study for the test.",
                  "I have (got) to clean my room."
                ],
                chinese: "必須、不得不"
              },
              {
                subtitle: "命令與警告",
                pattern: "must + base verb",
                usage: "Express strong commands or warnings",
                examples: [
                  "You must do as you are told.",
                  "You must return the book to the library today."
                ],
                chinese: "命令、警告"
              },
              {
                subtitle: "邏輯推論",
                pattern: "must + base verb",
                usage: "Express certain logical inference",
                examples: [
                  "All living things must die.",
                  "It must have rained last night, for the ground was wet this morning."
                ],
                chinese: "肯定的推測"
              },
              {
                subtitle: "否定形式",
                patterns: {
                  "must not": "Strong prohibition",
                  "don't have to": "Not necessary (similar to don't need to)"
                },
                examples: [
                  "Students must not park here.",
                  "You don't have to donate blood if you are so afraid."
                ]
              }
            ]
          },
          {
            subtitle: "Shall / Should",
            sections: [
              {
                subtitle: "Shall/Should 用法",
                patterns: {
                  "shall": "1. More formal than 'will' 2. Making suggestions (like let's, how about) 3. Should as past form of shall",
                  "should": "Advice, recommendation, or moral obligation"
                },
                examples: [
                  "Shall I buy some groceries on my way home?",
                  "You should donate most of the money to charity."
                ]
              },
              {
                subtitle: "Should/Ought to/Had better 程度",
                scale: "should → ought to → had better (increasing strength)",
                examples: [
                  "You shouldn't smoke around children.",
                  "The children ought not to play so close to the road.",
                  "You'd better leave right now."
                ]
              }
            ]
          },
          {
            subtitle: "特殊情態動詞: Dare / Need / Used to",
            sections: [
              {
                subtitle: "Dare",
                usage: "Express courage or challenge",
                patterns: {
                  "auxiliary": "dare + V / dare not + V",
                  "main verb": "dare(s) to + V / don't dare + V"
                },
                examples: [
                  "How dare you call me a coward!",
                  "Peter dared to go skydiving last weekend."
                ]
              },
              {
                subtitle: "Need",
                patterns: {
                  "auxiliary": "needn't + V / Need + S + V?",
                  "main verb": "need to + V / don't need to + V"
                },
                examples: [
                  "You needn't make a shopping list.",
                  "Does Mary need to go to the hospital?"
                ]
              },
              {
                subtitle: "Used to",
                pattern: "used to + base verb",
                usage: "Express past habits or situations",
                examples: [
                  "I used to get up early, but now I don't.",
                  "That Italian restaurant used to be a Mexican restaurant."
                ],
                distinction: {
                  "used to + V": "Past habits (followed by base verb)",
                  "be/get used to + V-ing/N": "Accustomed to something (followed by gerund/noun)"
                }
              }
            ]
          },
          {
            subtitle: "進階情態動詞用法",
            sections: [
              {
                subtitle: "進行式與完成式",
                pattern: "Modal + be + V-ing / Modal + have been + V-ing",
                examples: [
                  "You could be sleeping right now.",
                  "You must not have been paying attention in class."
                ]
              },
              {
                subtitle: "完成式",
                patterns: {
                  "could/would/should/might + have + p.p.": "Past hypothetical or contrary to fact",
                  "could/may/might + have + p.p.": "Past possibility (but didn't happen)",
                  "should/ought to + have + p.p.": "Past obligation (but didn't happen)"
                },
                examples: [
                  "You could have scored higher on the test, but you didn't bother to study.",
                  "We may have caught the last bus home.",
                  "You should not have come back so late."
                ]
              }
            ]
          }
        ]
      },

      // Practice Questions (combined from both files)
      practiceQuestions: [
        {
          question: "Choose the correct auxiliary verb: 'She ___ speak three languages when she was young.'",
          options: ["can", "could", "may", "might"],
          correct: 1,
          explanation: "Use 'could' for past ability."
        },
        {
          question: "Fill in the blank: '___ I borrow your pen?'",
          options: ["Will", "Would", "May", "Must"],
          correct: 2,
          explanation: "'May' is used for polite permission requests."
        },
        {
          question: "Which sentence shows possibility? 'It ___ rain tomorrow.'",
          options: ["will", "might", "must", "shall"],
          correct: 1,
          explanation: "'Might' expresses possibility with uncertainty."
        },
        {
          question: "Choose the most polite request: '___ you help me with this?'",
          options: ["Can", "Could", "Will", "May"],
          correct: 1,
          explanation: "'Could' is more polite than 'can' for requests."
        },
        {
          question: "Complete: 'You ___ not smoke in here.' (prohibition)",
          options: ["may", "might", "must", "could"],
          correct: 2,
          explanation: "'Must not' expresses prohibition."
        },
        {
          question: "_____ you stop harassing me? I'm telling the teacher!",
          options: ["Do", "Are", "Did", "Will"],
          correct: 3,
          explanation: "Will you is used for requests"
        },
        {
          question: "He said he _____ go abroad for further studies after graduation.",
          options: ["is", "would", "want to", "loves"],
          correct: 1,
          explanation: "Would is used in reported speech"
        }
      ],

      // Memory Tips
      memoryTips: {
        title: "記憶技巧 Memory Tips",
        tips: [
          {
            category: "能力助動詞",
            content: "Can (現在) → Could (過去) → Be able to (所有時態)"
          },
          {
            category: "可能性程度",
            content: "Might (低) → May (中) → Will (高) → Must (確定)"
          },
          {
            category: "禮貌程度",
            content: "Can → Could → Would → May (最禮貌)"
          },
          {
            category: "情態動詞記憶口訣",
            content: "🎭 WOULD = Polite & Preferences (Would you? / Would rather)"
          },
          {
            category: "必要性表達",
            content: "💪 MUST = Strong necessity/certainty (Must do / Must be true)"
          },
          {
            category: "建議與忠告",
            content: "📋 SHALL/SHOULD = Suggestions & Advice (Shall we? / You should)"
          },
          {
            category: "特殊情態動詞",
            content: "⭐ SPECIAL = Dare/Need/Used to (Courage/Necessity/Past habits)"
          },
          {
            category: "進階用法",
            content: "🎓 ADVANCED = Perfect forms (could have, should have, etc.)"
          }
        ]
      }
    }
  }
];

// Export individual units for specific access
export const getUnitById = (unitId) => {
  return grammarUnits4Data.find(unit => unit.id === unitId);
};

// Export units by difficulty
export const getUnitsByDifficulty = (difficulty) => {
  return grammarUnits4Data.filter(unit => unit.difficulty === difficulty);
};

// Export completed units
export const getCompletedUnits = () => {
  return grammarUnits4Data.filter(unit => unit.status === 'completed');
};

// Default export
export default grammarUnits4Data;
